// lib/formatters/formatPosts.ts
import { Images } from "@assets/assets";
import { PortfolioAllListInterface } from "@/lib/Types";
import type { TOCItem } from "@/lib/Types";
import MetaData from "./MetaData";
import { Category } from "./Interface";
type RawImage = {
  source_url?: string;
  url?: string;
  width?: number;
  height?: number;
  mime_type?: string;
};

type RawBlog = {
  title?: { rendered?: string } | string;
  excerpt?: { rendered?: string } | string;
  yoast_head?: string;
  link?: string;
  slug?: string;
  date?: string;
  modified?: string;
  author_name?: string;
  author?: number | { name?: string };
  yoast?: any;
  featured_media?: RawImage | null;
  better_featured_image?: RawImage | null;
  jetpack_featured_media_url?: string;
  _embedded?: any;
  schema?: any;
  [k: string]: any;
};

export interface Window {
  dataLayer: Record<string, any>[];
}

export interface BlogPost {
  id: number;
  title: string;
  excerpt: string;
  link: string;
  slug: string;
  image: string;
  author: string;
  date: string;
  publishedDate: string;
  categoryId?: number;
}

export interface catInterface {
  id: number;
  name: string;
  slug: string;
  link: string;
  count: string;
  description: string;
  seoTitle: string;
  seoMeta: string;
  postsUrl?: number;
}

export interface DetailedBlog {
  id: number;
  title: string;
  excerpt: string;
  content: string;
  link: string;
  slug: string;
  image: string;
  categoryName: string;
  author: string;
  authorDesc: string;
  authorImage: string;
  blogQoute: string;
  date: string;
  publishedDate: string;
  categoryId?: number;
  tableOfContent?: any[]; // Table of contents items from WordPress
  yoastData: any;
}

export {
  rewriteLegacyBlogUrls,
  rewriteLegacyBlogUrlsDeep,
} from "@/lib/LegacyBlogUrls";

// WordPress auto-generates resized copies named `name-<W>x<H>.ext` (e.g.
// `why-mobile-app-users-300x144.webp`, a 300x144 crop). The featured-media
// `source_url` sometimes points at one of these small crops, which then gets
// upscaled in the UI and looks blurry. Strip the `-WxH` suffix to load the
// full-size original (`why-mobile-app-users.webp`, 1040x500) instead.
export function toFullSizeWpImage(url: string): string {
  if (typeof url !== "string" || !url) return url;
  return url.replace(/-\d+x\d+(?=\.[a-zA-Z0-9]+$)/, "");
}

//Blog Grid View Formatter
export function formatWordPressPosts(array: any): BlogPost[] {
  if (!array || !Array.isArray(array)) return [];
  return array?.map((post: any) => ({
    id: post.id,
    title: post.title?.rendered || "",
    excerpt: post.yoast_head_json?.description || "",
    link: post.link || "",
    slug: `blog/${post.slug}`,
    image:
      toFullSizeWpImage(
        post._embedded?.["wp:featuredmedia"]?.[0]?.source_url || "",
      ) || Images?.imagePlaceholder,
    author: post._embedded?.author?.[0]?.name || "Admin",
    // Pin the locale: a bare toLocaleDateString() uses the runtime's default
    // locale, so the server (Node, en-US → "M/D/YYYY") and the browser (e.g.
    // en-GB/en-IN → "D/M/YYYY") produced different strings. formatDate() then
    // re-parsed them with new Date(), giving different/Invalid dates on each
    // side → React #418 hydration mismatch. en-US keeps the "M/D/YYYY" order
    // that new Date() parses unambiguously on every runtime.
    date: new Date(post.modified).toLocaleDateString("en-US"),
    publishedDate: new Date(post.date).toLocaleDateString("en-US"),
    categoryId: post?.categories?.[0],
  }));
}

//Blog Category List View Formatter
export function formatWordPressCategory(array: any): catInterface[] {
  if (!array || !Array.isArray(array)) return [];
  return array?.map((cat: any) => ({
    id: cat.id,
    name: cat.name,
    slug: cat.slug,
    link: cat.link,
    count: cat.count,
    description: cat.description || "",
    seoTitle: cat?.yoast_head_json?.title || "",
    seoMeta: cat?.yoast_head_json || {},
    postsUrl: cat._links?.["wp:post_type"]?.[0]?.href || "",
  }));
}

//Blog Details Page View Formatter
export function formatWordPressDetailedBlog(array: any): DetailedBlog[] {
  if (!array || !Array.isArray(array)) return [];
  console.log(array, "array")
  return array?.map((post: any) => ({
    id: post.id,
    title: post.title.rendered,
    excerpt: post.excerpt.rendered,
    content: post?.content?.rendered,
    link: post.link,
    slug: post.slug,
    image:
      toFullSizeWpImage(
        post._embedded?.["wp:featuredmedia"]?.[0]?.source_url || "",
      ) || Images?.imagePlaceholder,
    categoryName: post._embedded?.["wp:term"]?.[0]?.[0]?.name || "",
    author: post._embedded?.author?.[0]?.name || "Admin",
    authorDesc: post._embedded?.author?.[0]?.description || "Admin",
    authorImage: post._embedded?.author?.[0]?.avatar_urls["96"] || "Admin",
    blogQoute: post.yoast_head_json?.description || "Admin",
    date: new Date(post.modified).toString(),
    publishedDate: new Date(post?.date).toString(),
    categoryId: post?.categories[0],
    tableOfContent: (post?.table_of_content ?? []) as TOCItem[],
    yoastData: post?.yoast_head_json || "",
  }));
}

//BlogSchema Generator
export function generateBlogSchema(selectedBlog: any) {
  if (!selectedBlog) return null;

  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: selectedBlog.title,
    image: selectedBlog.image,
    author: {
      "@type": "Person",
      name: selectedBlog.author,
    },
    publisher: {
      "@type": "Organization",
      name: "Konstant Infosolutions",
      logo: {
        "@type": "ImageObject",
        url: "https://www.konstantinfo.com/assets/images/logo.png",
      },
    },
    datePublished: selectedBlog.publishedDate,
    dateModified: selectedBlog.date,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": selectedBlog.link,
    },
    description: selectedBlog?.yoastData?.description,
  };
}

export function generateServiceSchema(serviceName: string, urlSlug: string, description?: string, reviewData?: any) {
  if (!serviceName) return null;
  
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": serviceName,
    "description": description || "A result-oriented mobile app development company in USA and India with 23+ years of experience- We build advanced web and mobile solutions with AI, Blockchain, Cloud and IoT. Convert your ideas into reality with Konstantinfo.",
    "serviceType": serviceName,
    "provider": {
      "@type": "Organization",
      "name": "Konstant Infosolutions"
    },
    "areaServed": "Global",
    "url": `https://www.konstantinfo.com/${urlSlug.replace(/^\//, '')}`
  };

  if (!reviewData) {
    return serviceSchema;
  }

  const productSchema = {
    "@context": "https://schema.org/",
    "@type": "Product",
    "name": serviceName,
    "description": description || "Konstantinfo is an award-winning mobile app development Company in India, USA, UK and UAE. Hire mobile app developers to build featured-rich apps.",
    "url": `https://www.konstantinfo.com/${urlSlug.replace(/^\//, '')}`,
    "brand": {
      "@type": "Brand",
      "name": "Konstantinfo"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": reviewData.ratingValue || "4.9",
      "ratingCount": reviewData.ratingCount || "173"
    },
    "review": reviewData.reviews || []
  };

  return [serviceSchema, productSchema];
}

export function generateWebPageSchema(selectedBlog: any) {
  if (!selectedBlog) return null;
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": `${selectedBlog}`,
    "description": "Leading web development company in India and the USA offering expert developers and premium web development services.",
    "image": "https://cdn-kpbff.nitrocdn.com/BIqbPXrNizxHMrZEuWjiIDigcWJtuMrr/assets/images/optimized/rev-70752cb/d3mds3ychln71.cloudfront.net/img/stiklogo.png",
    "sku": `${selectedBlog}`,
    "category": "Software Development",
    "offers": {
      "@type": "AggregateOffer",
      "priceCurrency": "USD",
      "lowPrice": "5000",
      "highPrice": "500000",
      "availability": "https://schema.org/InStock",
      "url": "https://www.konstantinfo.com/web-development.php"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "173"
    },
    "review": [
      {
        "@type": "Review",
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": "5"
        },
        "reviewBody": "Konstant Infosolutions helped us significantly improve our customer service efficiency while reducing manual paperwork. They also streamlined our claim processing time and enhanced overall customer engagement through the mobile app. Their professional approach to project management ensured everything was delivered on schedule without any issues.",
        "author": {
          "@type": "Person",
          "name": "Longet Laurence",
          "jobTitle": "Director"
        }
      },
      {
        "@type": "Review",
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": "5"
        },
        "reviewBody": "Konstant Infosolutions helped us boost our user productivity by 40%. They managed the project exceptionally well, delivering everything on time while maintaining proactive communication throughout. The team was highly adaptable to changes and remained transparent at every stage. Their ability to integrate complex AI features seamlessly was particularly impressive.",
        "author": {
          "@type": "Person",
          "name": "Jerry Lee",
          "jobTitle": "Managing Director, Process AG"
        }
      }
    ]
  }
}

//FAQ Schema Generator
export function generateFAQSchema(
  faqs: { question: string; answer: string }[],
) {
  if (!faqs?.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs?.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        // remove HTML tags to keep schema text clean and safe for Google
        text: faq.answer.replace(/<[^>]*>/g, "").trim(),
      },
    })),
  };
}

//Portfolio Data Formatter
export function getPortfolioProject(array: any): PortfolioAllListInterface[] {
  if (!array || !Array.isArray(array)) return [];
  return array?.map((post: any, index: number) => ({
    id: post?.id ?? "",
    title: post?.name ?? "",
    description: post?.description ?? "",
    categories: post?.type ?? "",
    homepageBg: `${process.env.NEXT_PUBLIC_AWS_BASE_URL}/${post?.newHomepageBg ?? ""}`,
    newSliderBg: `${process.env.NEXT_PUBLIC_AWS_BASE_URL}/${post?.newSliderBg ?? ""}`,
    thumbBg: `${process.env.NEXT_PUBLIC_AWS_BASE_URL}/${post?.thumbImage ?? ""}`,
    colspan: index % 3 === 0 ? 2 : 1,
  }));
}

//Testimonials Data Formatter
export function mapTestimonialsArray(responseArray: any) {
  if (!Array.isArray(responseArray)) return [];
  return responseArray?.map((res) => {
    const portfolio = res.portfolio || {};
    const surveys = res.surveys || {};

    // Parse title JSON safely
    let parsedTitle: any = {};
    try {
      parsedTitle = JSON.parse(portfolio.title || "{}");
    } catch {
      parsedTitle = {};
    }

    return {
      id: surveys.id || 0,
      companyName: portfolio?.name || "",
      tagline: "Category Tag",
      companyLogoSrc: `https://d3q8mqotzsvo6s.cloudfront.net/${portfolio?.logoIcon}`, // Modify as needed
      ratingBadgeSrc: res?.surveys ? Images?.rating : null,
      quote: surveys.overallOverview?.replace(/<[^>]+>/g, "") || res?.feedback,
      authorName: parsedTitle?.ClientName || "",
      authorTitle: parsedTitle?.Designation || "",
      authorAvatarSrc: Images?.testimonial.user1, // Adjust to your asset path
      variant: "white",
      testimonialType: res?.testimonialType ?? 1,
      clientVideo: res?.video || "",
      rating: res?.rating,
    };
  });
}

//Blog List View Formatter
export function formatWordPressCarouselPosts(array: any): BlogPost[] {
  if (!array || !Array.isArray(array)) return [];
  return array?.map((post: any) => ({
    id: post.id,
    title: post.title?.rendered || "",
    excerpt: post.excerpt?.rendered || "",
    link: post.link || "",
    slug: `blog/${post.slug}`,
    image: toFullSizeWpImage(
      post._embedded?.["wp:featuredmedia"]?.[0]?.source_url || "",
    ),
    author: post._embedded?.author?.[0]?.name || "Admin",
    // Pin the locale: a bare toLocaleDateString() uses the runtime's default
    // locale, so the server (Node, en-US → "M/D/YYYY") and the browser (e.g.
    // en-GB/en-IN → "D/M/YYYY") produced different strings. formatDate() then
    // re-parsed them with new Date(), giving different/Invalid dates on each
    // side → React #418 hydration mismatch. en-US keeps the "M/D/YYYY" order
    // that new Date() parses unambiguously on every runtime.
    date: new Date(post.modified).toLocaleDateString("en-US"),
    publishedDate: new Date(post.date).toLocaleDateString("en-US"),
    categoryId: post?.categories?.[0],
  }));
}

export function normalizeFileType(mime: string): string | null {
  // Reject audio/video immediately
  if (mime.startsWith("audio/") || mime.startsWith("video/")) {
    return null;
  }

  switch (mime) {
    // Images
    case "image/jpeg":
      return "jpg";
    case "image/png":
      return "png";
    case "image/webp":
      return "webp";

    // Documents
    case "application/pdf":
      return "pdf";

    case "application/msword":
      return "doc";
    case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      return "docx";

    case "application/vnd.ms-excel":
      return "xls";
    case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
      return "xlsx";

    case "application/vnd.ms-powerpoint":
      return "ppt";
    case "application/vnd.openxmlformats-officedocument.presentationml.presentation":
      return "pptx";

    case "text/plain":
      return "txt";
    case "text/csv":
      return "csv";
    case "application/json":
      return "json";
    case "application/rtf":
      return "rtf";

    case "application/vnd.oasis.opendocument.text":
      return "odt";
    case "application/vnd.oasis.opendocument.spreadsheet":
      return "ods";
    case "application/vnd.oasis.opendocument.presentation":
      return "odp";

    default:
      return null; // unsupported
  }
}

export const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date
    .toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })
    .replace(" ", " ")
    .replace(" ", ", ");
};

export function extractWordPressMetaData(data: any) {
  if (!data || !Array.isArray(data)) return [];
  return data?.map((item: any) => {
    const yoast = item?.yoast_head_json || item || {};
    const ogImage = Array.isArray(yoast.og_image)
      ? yoast.og_image[0]?.url || yoast.og_image[0]
      : yoast.og_image?.url || yoast.og_image || "";
    return {
      title: yoast.title || "",
      description: yoast.description || "",
      ogTitle: yoast.og_title || yoast.title || "",
      ogDescription: yoast.og_description || yoast.description || "",
      ogImage,
      twitterCard: yoast.twitter_card || "summary_large_image",
    };
  });
}

function asString(v: any) {
  if (!v && v !== 0) return "";
  if (typeof v === "string") return v;
  if (typeof v === "object" && v?.rendered) return String(v.rendered);
  return String(v);
}

function pickImage(raw: RawBlog): RawImage | null {
  if (raw.featured_media && typeof raw.featured_media === "object")
    return raw.featured_media as RawImage;
  if (
    raw.better_featured_image &&
    typeof raw.better_featured_image === "object"
  )
    return raw.better_featured_image as RawImage;
  if (raw.jetpack_featured_media_url)
    return { url: raw.jetpack_featured_media_url };
  // try embedded media
  try {
    const embeds = raw._embedded;
    if (embeds?.["wp:featuredmedia"] && embeds["wp:featuredmedia"][0]) {
      const fm = embeds["wp:featuredmedia"][0];
      if (fm.media_details?.sizes?.full?.source_url)
        return { url: fm.media_details.sizes.full.source_url };
      if (fm.source_url) return { url: fm.source_url };
    }
  } catch {
    // Silently handle error
  }
  return null;
}

export function formateBlogMetaFormatter(raw: RawBlog) {
  // title / description
  const title = asString(raw.title ?? raw.headline ?? raw.seoTitle ?? "");
  const description = raw?.og_description || raw?.description;
  // robots object
  const robots = {
    index: "index",
    follow: "follow",
    "max-snippet": "max-snippet:-1",
    "max-image-preview": "max-image-preview:large",
    "max-video-preview": "max-video-preview:-1",
    ...(raw.yoast && raw.yoast.robots ? raw.yoast.robots : {}),
  };

  // canonical
  const canonical =
    raw.link ||
    raw.canonical ||
    (raw.slug
      ? `${process.env.NEXT_PUBLIC_BASE_URL ?? ""}/blog/${raw.slug}/`
      : "");

  // og fields
  const og_locale = raw.og_locale || raw.og?.locale || "en_US";
  const og_type = raw.og_type || raw.og?.type || "article";
  const og_title = raw.og_title || title;
  const og_description = raw.og_description || description;
  const og_url = raw.og_url || canonical;
  const og_site_name =
    raw.og_site_name || (process.env.NEXT_PUBLIC_SITE_NAME ?? "Konstantinfo");

  // article meta
  const article_author =
    raw.article_author ||
    raw.author_url ||
    raw.author_link ||
    raw.yoast?.author ||
    "";
  const article_published_time =
    raw.article_published_time || raw.date || raw.published || "";
  const article_modified_time = raw.article_modified_time || raw.modified || "";

  // og_image array
  const picked: any = pickImage(raw);
  const og_image =
    raw.og_image && Array.isArray(raw.og_image) && raw.og_image.length
      ? raw.og_image
      : picked
        ? [
          {
            url: (picked.url ?? picked.source_url ?? "").trim(),
            width: picked.width ?? 1200,
            height: picked.height ?? 630,
            type: picked.mime_type ?? "image/webp",
          },
        ]
        : [];

  // author name
  const author =
    raw.author_name ||
    (typeof raw.author === "object" ? raw.author.name : raw.author) ||
    raw.byline ||
    "";

  // twitter fields
  const twitter_card = raw.twitter_card || "summary_large_image";
  const twitter_creator =
    raw.twitter_creator ||
    raw.twitter?.creator ||
    raw.yoast?.twitter?.creator ||
    "";
  const twitter_site =
    raw.twitter_site || raw.twitter?.site || raw.yoast?.twitter?.site || "";
  const twitter_misc =
    raw.twitter_misc || (raw.meta && raw.meta.twitter_misc) || {};

  // schema (if already generated or embedded)
  const schema = raw.schema || raw._schema || raw.seo_graph || null;

  return {
    title: title,
    description: description,
    robots,
    canonical,
    og_locale,
    og_type,
    og_title,
    og_description,
    og_url,
    og_site_name,
    article_author,
    article_published_time,
    article_modified_time,
    og_image,
    author,
    twitter_card,
    twitter_creator,
    twitter_site,
    twitter_misc,
    schema,
  };
}

export function formatDateToDDMMYYYY(dateString: string): string {
  const date = new Date(dateString);
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

export function formatDateMMDDYY(dateString: string): string {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });
}

export function getMetaObj(slug: string) {
  if (!slug || slug.trim() === "") {
    slug = "/";
  }

  const meta = MetaData[slug];

  return {
    title: meta?.title || "",
    description: meta?.metaDescription || "",
    icons: {
      icon: `${process.env.NEXT_PUBLIC_CDN_URL}/favicon.ico`,
    },
    robots: {
      index: meta?.follow !== false,
      follow: meta?.follow !== false,
    },
    alternates: {
      canonical: `${process.env.NEXT_PUBLIC_BASE_URL}${slug}`,
    },
    openGraph: {
      title: meta?.title || "",
      description: meta?.metaDescription || "",
      siteName: "Konstantinfo",
      type: "website",
      locale: "en_US",
      images: [`${process.env.NEXT_PUBLIC_CDN_URL}/favicon.ico`],
    },
    twitter: {
      card: "summary_large_image",
      title: meta?.title || "",
      description: meta?.metaDescription || "",
      site: "@konstantinfo",
      creator: "@konstantinfo",
    },
  };
}

export function getRandom(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export async function getCountryCodeAccordingCountry() {
  if (typeof window === "undefined") return null;
  try {
    const response = await fetch("https://ipwho.is/");
    if (!response.ok) {
      console.log("Unable to fetch Data");
    }
    const data: any = await response.json();
    if (data.status === "fail") throw new Error(data.message);
    return {
      country_code: data?.countryCode || "+91",
      country_calling_code: data?.callingCode ? `+${data.callingCode}` : null,
    };
  } catch {
    return [];
  }
}

export function getCookie(name: string) {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? decodeURIComponent(match[2]) : null;
}

export function setCookie(name: string, value: string, days = 7) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/`;
}

export function getYouTubeId(url: string) {
  const regExp = /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([^&?/]+)/;
  const match = url.match(regExp);
  return match ? match[1] : null;
}

export function isYouTubeUrl(url: string) {
  return /youtube\.com|youtu\.be/.test(url);
}

export const trackRequestQuoteModal = () => {
  if (typeof window !== "undefined") {
    window.dataLayer = window.dataLayer || [];

    window.dataLayer.push({
      event: "virtual_pageview",
      page_title: "Request A Quote",
      page_path: "/request-a-quote",
      page_location: window.location.href,
    });
  }
};

export const blogFormatDateOLD = (inputDate: string) => {
  try {
    let date: Date;
    if (/^\d{4}-\d{2}-\d{2}/.test(inputDate)) {
      date = new Date(inputDate);
    } else {
      const [day, month, year] = inputDate.split("/");
      if (day && month && year) {
        date = new Date(
          `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`,
        );
      } else {
        date = new Date(inputDate);
      }
    }
    return date;
  } catch (err: any) {
    console.log(err?.message, "====> blogFormatDate");
  }
};


export const blogFormatDate = (inputDate: string): Date | null => {
  try {
    let date: Date;
    if (/^\d{4}-\d{2}-\d{2}/.test(inputDate)) {
      date = new Date(inputDate);
    }
    else if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(inputDate)) {
      const [month, day, year] = inputDate.split("/");
      date = new Date(
        `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`,
      );
    } else {
      date = new Date(inputDate);
    }
    return isNaN(date.getTime()) ? null : date;
  } catch (err: any) {
    console.error(err?.message, "====> blogFormatDate");
    return null;
  }
};

export const stringDateFormat = (inputDate: string) => {
  try {
    const [day, month, year] = inputDate.split("/");
    const convertedDate = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
    );
    const formattedDate = convertedDate.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });

    return formattedDate;
  } catch (err: any) {
    console.log(err?.message, "====> blogFormatDate");
  }
};

export const parseBrokenTitle = (rawTitle = "") => {
  const getValue = (key: string) => {
    const match = rawTitle.match(
      new RegExp(`"${key}"\\s*:\\s*"([^"]*)`, "i"),
    );
    return match ? match[1] : "";
  };

  return {
    ClientName: getValue("ClientName"),
    Designation: getValue("Designation"),
    CompanyName: getValue("CompanyName"),
  };
};

export const cleanText = (html: string) =>
  html
    .replace(/<[^>]*>/g, "")
    .replace(/^"+|"+$/g, "")
    .trim();

function normalize(s: string) {
  return s
    .toLowerCase()
    .trim()
    .replace(/^\//, "")
    .replace(/\/$/, "")
    .replace(/\s+/g, "-");
}

export const getBlogPostId = (slug: string, type?: string, categoriesJson: Category[] = [],) => {
  const key = normalize(slug);

  const match = (categoriesJson as any[]).find(
    (entry) => entry?.slug && normalize(entry.slug) === key,
  );

  if (!match) return null;

  return {
    id: Number(match.id),
    type: type ?? match.type ?? "category",
  };
};
